import {
  overwriteImageAtPath,
  refreshFirebaseDownloadUrl,
  storageLocationFromDownloadUrl,
  thumbnailDownloadUrl,
} from "@/shared/lib/firebaseStorage";
import { normalizeRotation, rotatedCanvasSize } from "./rotateAssetPhoto.logic";

export interface RotateAssetPhotoInput {
  /** The photo's stored URL — `asset_documents.file_path` or an asset column. */
  url: string;
  /** 90 / 180 / 270, as accumulated by the viewer's rotate buttons. */
  degrees: number;
  /** Upload progress, 0..100. */
  onProgress?: (percent: number) => void;
}

export interface RotateAssetPhotoResult {
  /**
   * Download URL after the overwrite. The object's download token is re-minted
   * by the upload, so this differs from the input URL and the caller MUST
   * persist it — the old URL now 403s.
   */
  downloadUrl: string;
  /** `gs://` location that was overwritten. */
  path: string;
}

/** JPEG, not PNG: these photos run 7–18 MB and PNG re-encoding inflates them. */
const OUTPUT_TYPE = "image/jpeg";
const OUTPUT_QUALITY = 0.9;

/**
 * Rotate an asset photo and persist it, overwriting the same Storage object.
 *
 * Same object path, always: `asset_documents` has no unique constraint and
 * dedupes on `split_part(file_path,'?',1)` (ENG-1034), and ~99% of prod rows have
 * a null or legacy-URL `ref_id`, so the path *is* the row's identity. Writing to
 * a new path would orphan it and make the next Firebase sync insert a duplicate.
 * Rewriting the row's `file_path` with a fresh token is fine — the token lives in
 * the query string, which that identity excludes.
 *
 * The upload mints a new download token, so every previously stored copy of this
 * URL 403s until it is refreshed. Site-manager's own copies are updated by the
 * caller and `<FirebaseImage>` re-mints anything missed; the Firestore form node
 * that shares these objects is NOT updated, so robotic-imaging-web and the Flutter
 * app will need to re-resolve. That is the mirror image of what web's own rotate
 * already does to Supabase.
 */
export async function rotateAssetPhoto({
  url,
  degrees,
  onProgress,
}: RotateAssetPhotoInput): Promise<RotateAssetPhotoResult> {
  const rotation = normalizeRotation(degrees);
  if (rotation === 0) throw new Error("Nothing to rotate");

  const result = await rotateStorageObject(url, rotation, onProgress);

  // The 250 px `_th.jpg` is a separate object and it is what the photo grids
  // render — leaving it un-rotated would show the old orientation everywhere
  // except this viewer. Its URL is derived and re-minted at render time, so
  // there is nothing to persist. Best-effort: Flutter-captured photos have no
  // thumbnail, and a thumbnail failure must not undo a good rotation.
  try {
    const thumbUrl = await thumbnailDownloadUrl(url);
    if (thumbUrl) await rotateStorageObject(thumbUrl, rotation);
  } catch (err) {
    console.warn("rotateAssetPhoto: thumbnail rotation failed", err);
  }

  return result;
}

/** Rotate one Storage object in place. */
async function rotateStorageObject(
  url: string,
  rotation: number,
  onProgress?: (percent: number) => void,
): Promise<RotateAssetPhotoResult> {
  // gs://bucket/path, not a bare path: these photos live in the prod bucket
  // whatever project this build points at, and a bare path would resolve against
  // the configured default bucket and overwrite the wrong object (or 404).
  const path = storageLocationFromDownloadUrl(url);
  if (!path) {
    throw new Error("This photo isn't stored in Firebase Storage, so it can't be rotated");
  }

  // The stored token may already be stale (e.g. the web app rotated this photo),
  // in which case loading the stored URL would 403 before we reach the canvas.
  const freshUrl = await refreshFirebaseDownloadUrl(url);
  const blob = await renderRotatedJpeg(freshUrl ?? url, rotation);

  const { downloadUrl } = await overwriteImageAtPath(path, blob, {
    onProgress: (fraction) => onProgress?.(Math.round(fraction * 100)),
  });

  return { downloadUrl, path };
}

/**
 * Draw the image rotated onto a canvas and re-encode it.
 *
 * `crossOrigin = "anonymous"` is required: reading pixels back out of the canvas
 * taints it otherwise. The bucket already serves CORS headers for this — the
 * same technique is in production in robotic-imaging-web's `handleRotateImage`.
 */
async function renderRotatedJpeg(url: string, degrees: number): Promise<Blob> {
  const img = new Image();
  img.crossOrigin = "anonymous";
  img.src = url;

  await new Promise<void>((resolve, reject) => {
    img.onload = () => resolve();
    img.onerror = () => reject(new Error("Could not load the photo for rotation"));
  });

  const canvas = document.createElement("canvas");
  const ctx = canvas.getContext("2d");
  if (!ctx) throw new Error("Canvas 2D context unavailable");

  const size = rotatedCanvasSize(img.naturalWidth, img.naturalHeight, degrees);
  canvas.width = size.width;
  canvas.height = size.height;

  ctx.translate(canvas.width / 2, canvas.height / 2);
  ctx.rotate((degrees * Math.PI) / 180);
  ctx.drawImage(img, -img.naturalWidth / 2, -img.naturalHeight / 2);

  return new Promise<Blob>((resolve, reject) => {
    canvas.toBlob(
      (blob) => (blob ? resolve(blob) : reject(new Error("Failed to encode the rotated photo"))),
      OUTPUT_TYPE,
      OUTPUT_QUALITY,
    );
  });
}
